import { OBFUSCATING_PLAID_BANK_PROVIDERS } from '@/data/obfuscating-plaid-bank-providers';
import { computed } from 'vue';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { ContextRoot } from '@/utils/context-root';
import { BankAccount } from '@/types/banking.types';
import { Contract } from '@/types/contract.types';
import { Advance } from '@/types/advance.types';

export default function (context: ContextRoot) {
  const ctx = context.root;
  const { getters } = ctx.$store;

  const bankAccounts = computed<BankAccount[]>(() => getters.bankAccounts);
  const businessId = computed<number>(() => getters.businessId);
  const currentBankAccountToBePrimary = computed<BankAccount | undefined>(
    () => getters.currentBankAccountToBePrimary,
  );
  const userHasActiveBillAdvances = computed<boolean>(
    () => getters.userHasActiveBillAdvances,
  );
  const primaryBankAccount = computed<BankAccount | undefined>(
    () => getters.primaryBankAccount,
  );
  const contracts = computed<Contract[]>(() => getters.contracts);
  const advances = computed<Advance[]>(() => getters.advances);

  const bankStatementsRequired = (bankAccountId: number) => {
    const selectedBankAccount = bankAccounts.value.find(
      (bankAccount) => bankAccount.id === bankAccountId,
    );

    return OBFUSCATING_PLAID_BANK_PROVIDERS.includes(
      selectedBankAccount?.bankName ?? '',
    );
  };

  /**
   * Adds 'setAsPrimary: true' to details column of a bank account.
   * Also unmarks the previous bank account as primary, if exists.
   * @param bankAccountId bank account id to set as primary
   */
  const markBankAccountToBecomePrimary = async (bankAccountId: number) => {
    const apiActions = [];
    if (
      currentBankAccountToBePrimary.value &&
      currentBankAccountToBePrimary.value.id !== bankAccountId
    ) {
      const currentBankAccountToBePrimaryDetails = {
        ...currentBankAccountToBePrimary.value.details,
      };

      delete currentBankAccountToBePrimaryDetails.setAsPrimary;

      // unmark currentBankAccountToBePrimary as primary
      const unmarkCurrentBankAccountAsPrimaryRequest =
        ctx.$store.dispatchApiAction('UPDATE_BANK_ACCOUNT', {
          id: currentBankAccountToBePrimary.value.id,
          details: currentBankAccountToBePrimaryDetails,
        });

      apiActions.push(unmarkCurrentBankAccountAsPrimaryRequest);
    }
    // mark new bankAccount as primary

    const newBankAccountToBePrimary = bankAccounts.value.find(
      (bankAccount) => bankAccount.id === bankAccountId,
    );

    const markNewBankAccountAsPrimaryRequest = ctx.$store.dispatchApiAction(
      'UPDATE_BANK_ACCOUNT',
      {
        id: bankAccountId,
        details: {
          ...newBankAccountToBePrimary?.details,
          setAsPrimary: true,
        },
      },
    );

    apiActions.push(markNewBankAccountAsPrimaryRequest);

    await Promise.all(apiActions);

    analytics.track(SEGMENT_EVENTS.plaid_mandate_primary_set_intent, {
      bankAccountId,
      businessId,
    });
  };

  const removeIntentFromAllBankAccounts = async (): Promise<void> => {
    const promises = bankAccounts.value.map((account) => {
      const { setAsPrimary, ...details } = account.details ?? {};
      return setAsPrimary
        ? ctx.$store.dispatchApiAction('UPDATE_BANK_ACCOUNT', {
            id: account.id,
            details,
          })
        : Promise.resolve();
    });

    await Promise.all(promises);
  };

  const setBankAccountAsPrimary = async (bankAccountId: number) => {
    if (primaryBankAccount.value?.id === bankAccountId) {
      return;
    }

    await removeIntentFromAllBankAccounts();

    await ctx.$store.dispatchApiAction('SET_BANK_ACCOUNT_AS_PRIMARY', {
      id: bankAccountId,
    });
  };

  const isAdvanceInNegotiation = (advanceId: number) => {
    const advance = advances.value.find((adv) => adv.id === advanceId);

    if (advance) {
      return !advance.activatedAt && !advance.rejectedAt;
    }

    return false;
  };

  const bankAccountHasActiveContract = (bankAccountId: number) => {
    const filteredContracts = contracts.value.filter(
      (contract) =>
        contract.bankAccountsId === bankAccountId &&
        contract.type === 'bnpl_bill',
    );

    return filteredContracts.some((contract) =>
      isAdvanceInNegotiation(contract.advanceId ?? 0),
    );
  };

  // Users can change their primary bank account as long as:
  // 1. They have no active bill advances
  // 2. They have no active contracts for their current primary bank account. A
  // contract is active if it's for an advance that's currently in negotiation
  const changePrimaryBlockers = () => {
    if (userHasActiveBillAdvances.value) {
      return 'Your current primary account is actively being debited';
    } else if (
      primaryBankAccount.value &&
      bankAccountHasActiveContract(primaryBankAccount.value.id)
    ) {
      return 'You have a contract in negotiation with your current primary bank account';
    } else if (
      currentBankAccountToBePrimary.value &&
      bankAccountHasActiveContract(currentBankAccountToBePrimary.value.id)
    ) {
      return 'The account you want to set as primary has a contract in negotiation';
    } else {
      return '';
    }
  };

  return {
    bankStatementsRequired,
    markBankAccountToBecomePrimary,
    setBankAccountAsPrimary,
    changePrimaryBlockers,
  };
}
